import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown } from "lucide-react";
import { LANGS } from "@/i18n";
import Flag from "@/components/Flag";

/**
 * Compact language switcher. Shows a clean inline SVG flag of the current
 * language (so it renders identically across every browser/OS — emoji flags
 * fall back to "PT" / "ES" on Windows and some Chrome installs) and a dropdown
 * with every supported language.
 */
export default function LanguageSwitcher({ tone = "light", compact = false }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const current = LANGS.find((l) => l.code === i18n.language) || LANGS[0];

  const choose = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  const colorBtn = tone === "light"
    ? "text-white/90 hover:text-white border-white/20 hover:border-white/40 bg-white/5 backdrop-blur"
    : "text-slate-700 hover:text-slate-900 border-slate-200 hover:border-slate-400 bg-white";

  return (
    <div className="relative inline-block" data-testid="lang-switcher">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-2 pl-2 pr-2.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${colorBtn}`}
        data-testid="lang-switcher-btn"
        aria-label="Language"
      >
        <Flag code={current.code} />
        {!compact && <span className="font-mono-fin tracking-wider">{current.code.toUpperCase()}</span>}
        <ChevronDown size={12} className="opacity-60" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 mt-2 w-48 rounded-2xl bg-white border border-slate-200 shadow-2xl z-50 py-1 overflow-hidden"
            data-testid="lang-switcher-menu"
          >
            {LANGS.map((l) => (
              <button
                key={l.code}
                onClick={() => choose(l.code)}
                data-testid={`lang-option-${l.code}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-slate-50 transition-colors ${
                  l.code === current.code ? "text-purple-700 font-semibold bg-purple-50/40" : "text-slate-700"
                }`}
              >
                <Flag code={l.code} />
                <span className="flex-1 text-left">{l.label}</span>
                {l.code === current.code && <Check size={14} className="text-purple-600" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
